/**
 * <scroll-progress> — drives a progress indicator bar from either a native
 * horizontal scroll container or a <swiper-component>.
 *
 * The element itself is the track; it must contain a single bar element marked
 * with [data-progress-bar]. The driver is resolved within the closest
 * `.shopify-section`.
 *
 * Attributes:
 *   data-mode="swiper"             — drive from the section's <swiper-component>
 *   data-mode="scroll" (default)   — drive from a native scroll container
 *   data-scroller="<selector>"     — scroll-container selector (default ".swipe-mobile")
 */
if (!customElements.get('scroll-progress')) {
  class ScrollProgress extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('[data-progress-bar]');
      if (!this.bar) return;

      this.mode = this.dataset.mode || 'scroll';
      this.update = this.update.bind(this);
      const root = this.closest('.shopify-section') || document;

      if (this.mode === 'swiper') {
        this.swiperEl = root.querySelector('swiper-component');
        if (!this.swiperEl) return;
        customElements.whenDefined('swiper-component').then(() => {
          requestAnimationFrame(() => this.initSwiper());
        });
      } else {
        this.scroller = root.querySelector(this.dataset.scroller || '.swipe-mobile');
        if (!this.scroller) return;
        this.scroller.addEventListener('scroll', this.update, { passive: true });
        window.addEventListener('resize', this.update);
        requestAnimationFrame(this.update);
      }
    }

    initSwiper() {
      this.swiper = this.swiperEl?.swiper;
      if (!this.swiper) return;
      this.update();
      this.swiper.on('progress', this.update);
      this.swiper.on('resize', this.update);
    }

    update() {
      const trackWidth = this.offsetWidth;
      let indicatorRatio;
      let progress;

      if (this.mode === 'swiper') {
        if (!this.swiper) return;
        const slidesPerView = parseFloat(this.swiper.params.slidesPerView) || 1;
        const totalSlides = this.swiper.slides.length;
        indicatorRatio = Math.min(slidesPerView / totalSlides, 1);
        progress = this.swiper.progress;
      } else {
        const scroller = this.scroller;
        const maxScroll = scroller.scrollWidth - scroller.clientWidth;
        indicatorRatio = scroller.scrollWidth > 0 ? Math.min(scroller.clientWidth / scroller.scrollWidth, 1) : 1;
        progress = maxScroll > 0 ? Math.abs(scroller.scrollLeft) / maxScroll : 0;
      }

      const indicatorWidth = indicatorRatio * trackWidth;
      this.bar.style.width = indicatorRatio * 100 + '%';
      this.bar.style.transform = `translateX(${progress * (trackWidth - indicatorWidth)}px)`;
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.update);
      this.scroller?.removeEventListener('scroll', this.update);
      this.swiper?.off('progress', this.update);
      this.swiper?.off('resize', this.update);
    }
  }

  customElements.define('scroll-progress', ScrollProgress);
}
